import { useRef, useState } from 'react';
import { Radio, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { fetchLine, fetchVoice } from '@/spore/api';
import { unlockAudio, decodeAudio, playEerie } from '@/spore/eerieAudio';
import { Panel } from './Panel';

const FEELINGS = ['curiosity', 'fear', 'trust', 'joy', 'grief', 'unity'];
const KINDS = ['intro', 'hint', 'reaction', 'reveal', 'finale'];

const Chips = ({ items, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((it) => (
      <button key={it} data-testid={`${prefix}-${it}`} className={`btn-lab px-3 py-1.5 text-[0.7rem] ${value === it ? 'active' : ''}`} onClick={() => onChange(it)}>
        {String(it)}
      </button>
    ))}
  </div>
);

const Stat = ({ label, value, testId, accent }) => (
  <div className="border-l border-[var(--hair-strong)] pl-3">
    <div className="label-eyebrow">{label}</div>
    <div data-testid={testId} className={`font-display text-2xl font-bold tabular-nums ${accent || ''}`}>{value ?? '—'}</div>
  </div>
);

export const VoicePanel = ({ gestures }) => {
  const [feeling, setFeeling] = useState('curiosity');
  const [kind, setKind] = useState('hint');
  const [hint, setHint] = useState(1);
  const [fx, setFx] = useState(true);
  const [phase, setPhase] = useState('idle');
  const [r, setR] = useState(null);
  const [history, setHistory] = useState([]);
  const lastBuffer = useRef(null);
  const player = useRef(null);

  const play = (buffer) => {
    if (player.current) player.current.stop();
    player.current = playEerie(buffer, { fx });
    setPhase('playing');
    player.current.ended.then(() => setPhase('idle'));
    return player.current;
  };

  const transmit = async () => {
    const unlocked = unlockAudio();
    const t0 = performance.now();
    setPhase('line');
    setR(null);
    try {
      await unlocked;
      const recent = gestures.slice(0, 8).reverse().map((g) => g.type + (g.seconds ? `(${g.seconds}s)` : ''));
      const line = await fetchLine({ feeling, kind, hint_level: kind === 'hint' ? hint : undefined, recent_gestures: recent.length ? recent : undefined });
      setR({ ...line });
      setPhase('voice');
      const voice = await fetchVoice(line.text);
      const td = performance.now();
      const buffer = await decodeAudio(voice.buffer);
      const decodeMs = Math.round(performance.now() - td);
      lastBuffer.current = buffer;
      const p = play(buffer);
      const totalMs = Math.round(performance.now() - t0);
      const res = { ...line, voiceServerMs: voice.serverMs, voiceClientMs: voice.clientMs, decodeMs, totalMs, duration: p.duration.toFixed(1), feeling, kind };
      setR(res);
      setHistory((h) => [res, ...h].slice(0, 6));
    } catch (e) {
      setPhase('idle');
      toast.error(`Transmission failed: ${e.message}`);
    }
  };

  const busy = phase === 'line' || phase === 'voice';
  return (
    <Panel testId="voice-panel" code="07 / TRANSMISSION" title="Hivemind Voice Test" delay={380} right={<Radio size={18} className={phase === 'playing' ? 'glow-spore dot-pulse' : 'text-[var(--teal)]'} />}>
      <div className="space-y-4">
        <div><div className="label-eyebrow mb-2">Feeling</div><Chips items={FEELINGS} value={feeling} onChange={setFeeling} prefix="feeling-select" /></div>
        <div><div className="label-eyebrow mb-2">Kind</div><Chips items={KINDS} value={kind} onChange={setKind} prefix="kind-select" /></div>
        <div className={kind === 'hint' ? '' : 'pointer-events-none opacity-30'}>
          <div className="label-eyebrow mb-2">Hint level</div>
          <Chips items={[0, 1, 2, 3]} value={hint} onChange={setHint} prefix="hint-level" />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="voice-test-transmission-button" className="btn-lab primary" disabled={busy} onClick={transmit}>
            {phase === 'line' ? 'Composing line…' : phase === 'voice' ? 'Synthesizing voice…' : 'Test transmission'}
          </button>
          <button data-testid="voice-replay-button" className="btn-lab flex items-center gap-2" disabled={!lastBuffer.current || busy} onClick={() => play(lastBuffer.current)}><RotateCcw size={14} /> Replay</button>
          <button data-testid="voice-fx-toggle" className={`btn-lab ${fx ? 'active' : ''}`} onClick={() => setFx(!fx)}>Eerie FX {fx ? 'on' : 'off'}</button>
          <span data-testid="voice-phase" className="text-xs tracking-[0.2em] text-[var(--muted)]">{phase.toUpperCase()}</span>
        </div>

        <div data-testid="voice-transmission-output" className="min-h-[96px] border border-[var(--hair)] bg-[#03060a] p-4">
          {r ? (
            <>
              <div className="mb-2 flex items-center gap-3 text-xs tracking-[0.2em]">
                <span data-testid="voice-line-source" className={`border px-2 py-0.5 ${r.source === 'llm' ? 'border-[var(--spore)] text-[var(--spore)]' : 'border-[var(--amber)] text-[var(--amber)]'}`}>{r.source === 'llm' ? `LLM · ${r.model}` : 'FALLBACK'}</span>
                {r.error && <span data-testid="voice-line-error" className="text-[var(--amber)]">{r.error}</span>}
              </div>
              <p data-testid="voice-line-text" className="font-display text-xl leading-snug md:text-2xl">“{r.text}”</p>
            </>
          ) : (
            <p className="text-sm text-[var(--dim)]">{busy ? 'Listening to the network…' : 'No transmission yet. Audio starts on click (autoplay-safe).'}</p>
          )}
        </div>

        <div data-testid="latency-breakdown-display" className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Stat label="Line (server)" value={r && r.ms != null ? `${r.ms}ms` : null} testId="latency-line-ms" />
          <Stat label="Line (round trip)" value={r && r.clientMs ? `${r.clientMs}ms` : null} testId="latency-line-client-ms" />
          <Stat label="Voice (server/rt)" value={r && r.voiceServerMs ? `${r.voiceServerMs}/${r.voiceClientMs}ms` : null} testId="latency-voice-ms" />
          <Stat label="Total → playback" value={r && r.totalMs ? `${r.totalMs}ms` : null} testId="latency-total-ms" accent="glow-spore" />
        </div>

        {history.length > 0 && (
          <div data-testid="voice-history" className="text-xs text-[var(--muted)]">
            <div className="label-eyebrow mb-1">Recent transmissions</div>
            {history.map((h, i) => (
              <div key={i} className="flex justify-between border-b border-white/5 py-1 tabular-nums">
                <span>{h.feeling}/{h.kind} · {h.source}</span>
                <span>line {h.ms} · voice {h.voiceServerMs} · total {h.totalMs}ms · {h.duration}s audio</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
};
